import { useJobs } from '../../features/jobs/hooks/useJobs';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import ProjectsTopSection from '../../components/project-management/ProjectsTopSection';
import ProjectMonitorListTable from '../../components/tables/ProjectMonitorListTable';
import { Badge, ProgressBar } from 'react-bootstrap';
import { useMemo } from 'react';
import { getJobStatusUI } from '../../helpers/jobStatus';
import ProductColorDot from '../../components/common/ProductColorDot';
import LengthSplit from '../../components/common/LengthSplit';
import TableExpandButton from '../../components/common/TableExpandButton';
import { FULL_TABLE_ROUTES, FULL_TABLE_PAGE_SIZE } from '../full-table/routes';
import { jobSearchKey, matchesSearch } from '../../helpers/cableSearch';
import {
  fusedPhasesOf,
  phaseSegmentLabel,
  phaseSegmentCovers
} from '../../helpers/tandemPhases';
// [ "unwinding", "pre_stranding", "conductor_isolation", "rope", "interior_sheat", "exterior_sheat", "rewinding", "coiling" ]

// src/features/jobs/constants/phasesOrder.js
export const PHASES_ORDER = [
  'unwinding',
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'exterior_sheat',
  'rewinding',
  'coiling'
];

export const PHASE_LABEL = {
  unwinding: 'Unwind',
  pre_stranding: 'Pre-Strand',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Inner',
  exterior_sheat: 'Outer',
  rewinding: 'Rewind',
  coiling: 'Coil'
};

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

// acceptă 0..1 sau 0..100
function toPercent(p) {
  const x = Number(p ?? 0);
  if (!Number.isFinite(x)) return 0;
  return x <= 1
    ? Math.round(clamp01(x) * 100)
    : Math.round(Math.max(0, Math.min(100, x)));
}

// Paletă stabilă (nu depinde de theme). Rewinding era gri și coiling negru —
// pe fundal întunecat amândouă dispăreau în bara goală din spatele lor, adică
// exact ultimele două faze, cele care spun dacă produsul e gata de livrat.
// Culorile se repetă pe alocuri și e în regulă — poziția și eticheta de sub
// bară fac distincția; ce contează e să se vadă pe ambele teme.
function phaseColor(phaseKey) {
  const map = {
    unwinding: 'info',
    pre_stranding: 'primary',
    conductor_isolation: 'primary',
    rope: 'warning',
    interior_sheat: 'info',
    exterior_sheat: 'success',
    rewinding: 'primary',
    coiling: 'success'
  };
  return map[phaseKey] || 'primary';
}

const PhaseProgressBar = ({ job }) => {
  const jobPhaseKeys = Object.keys(job?.phases || {});
  const order = job?.phasesForGant?.length
    ? job.phasesForGant
    : jobPhaseKeys.length
      ? [...jobPhaseKeys].sort((a, b) => {
          const ia = PHASES_ORDER.indexOf(a);
          const ib = PHASES_ORDER.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        })
      : PHASES_ORDER;
  const phases = job?.phases || {};

  const fused = useMemo(() => fusedPhasesOf(job), [job]);

  const rows = useMemo(() => {
    return order.map(phaseKey => {
      const phase = phases?.[phaseKey];
      const exists = !!phase;
      const percent = exists ? toPercent(phase.progress) : 0;
      const together = fused[phaseKey];

      return {
        phaseKey,
        label: phaseSegmentLabel(phaseKey, fused, p => PHASE_LABEL[p] || p),
        // How many phases this one segment covers — the count below the bar has
        // to agree with the labels, or a job reads as having fewer phases than
        // its plan.
        covers: phaseSegmentCovers(phaseKey, fused),
        together: together || null,
        exists,
        percent
      };
    });
  }, [order, phases, fused]);

  const live = rows.filter(r => r.exists);
  // Counted in PHASES, not in segments. A fused pass is one bar but two phases,
  // and counting segments made a 4-phase product read as "2 / 3" the moment it
  // was launched in tandem — the number quietly dropped as work started.
  const counted = live.length ? live : rows;
  const totalPhases = counted.reduce((s, r) => s + r.covers, 0);
  const done = counted
    .filter(r => r.exists && r.percent >= 100)
    .reduce((s, r) => s + r.covers, 0);

  // Deliberately the same three-row shape as LengthSplit next door — caption,
  // bar, caption — with the bar at the same height and the same gap above it.
  // The two columns are read across, and bars sitting two pixels apart on
  // different baselines is the kind of thing that makes a table feel wrong
  // without anybody being able to say why.
  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 260 }}>
      <span className="fw-semibold fs-9">
        {done} / {totalPhases} phases
      </span>

      {/* “segmented” look */}
      <div className="d-flex gap-1 align-items-center" style={{ height: 6 }}>
        {rows.map(r => (
          <div
            key={r.phaseKey}
            title={
              r.together
                ? `${r.label}: ${r.exists ? r.percent : 0}% — extruded together in one pass`
                : `${r.label}: ${r.exists ? r.percent : 0}%`
            }
            style={{
              flex: r.covers,
              opacity: r.exists ? 1 : 0.25,
              filter: r.exists ? 'none' : 'grayscale(1)'
            }}
          >
            <ProgressBar
              now={r.percent}
              variant={phaseColor(r.phaseKey)}
              style={{
                height: 6,
                borderRadius: 999,
                background: 'rgba(148, 163, 184, 0.25)' // slate-ish
              }}
            />
          </div>
        ))}
      </div>

      {/* labels mici sub bară */}
      <div className="d-flex gap-1">
        {rows.map(r => (
          <div
            key={r.phaseKey + '_lbl'}
            style={{
              flex: r.covers,
              textAlign: 'center',
              opacity: r.exists ? 0.9 : 0.25,
              whiteSpace: 'nowrap'
            }}
            className="text-700 fs-10"
          >
            {r.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const projectListTableColumns = [
  {
    accessorKey: 'jobIdShort',
    header: 'Job ID',
    cell: ({ row: { original } }) => {
      return (
        <Link
          to={`/apps/project-management/project-details/${original.jobId}`}
          className="text-decoration-none fw-bold fs-8"
          title={original.jobId} // hover arată full id
        >
          {original.jobId.slice(0, 8).toUpperCase()} ...
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'name',
    header: 'Project Name',
    cell: ({ row: { original } }) => {
      return (
        <Link
          to={`/apps/standard/${original.standardId}`}
          className="text-decoration-none fw-bold fs-8 d-inline-flex align-items-center gap-2"
        >
          <ProductColorDot row={original} />
          {original.productLabel}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'P.Length (m)',
    // The same number, plus the only thing anybody was doing arithmetic on it
    // for: how much of the lot is still free to promise.
    cell: ({ row: { original } }) => <LengthSplit job={original} />,
    // The padding was on an inner div here and on the cell over in Phases, so
    // one column carried the table's own cell padding on top of its own and sat
    // lower than its neighbour. Both on the cell now.
    meta: {
      cellProps: { className: 'ps-3 py-3 align-top' },
      headerProps: { style: { minWidth: 150 } }
    }
  },
  {
    id: 'progressPhases',
    header: 'Phases',
    cell: ({ row: { original } }) => {
      return <PhaseProgressBar job={original} />;
    },
    meta: {
      cellProps: { className: 'py-3 align-top' },
      headerProps: { style: { width: '100%' } }
    }
  },
  {
    id: 'status',
    header: 'Status',
    // `status` is the raw string off the job here (this table does not go
    // through jobsAdapter), so `.label` was undefined on every row and sorting
    // by Status did nothing at all.
    accessorFn: ({ status }) => getJobStatusUI(status).label,
    // Exact, not substring: the default string filter would make the "Active"
    // tab match "inactive" too.
    filterFn: 'equalsString',
    cell: ({ row: { original } }) => {
      const { status } = original;
      const usingStatus = getJobStatusUI(status);
      return (
        <Badge variant="phoenix" bg={usingStatus.type}>
          {usingStatus.label.toUpperCase()}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  }
];

// `fullPage` = rendered on its own dedicated route rather than embedded in
// Project Monitor, so it can afford a much taller page of projects.
const ProjectJobsMonitor = ({ jobs, fullPage = false }) => {
  const memoJobs = useMemo(() => jobs, [jobs]);

  const table = useAdvanceTable({
    data: memoJobs,
    columns: projectListTableColumns,
    pageSize: fullPage ? FULL_TABLE_PAGE_SIZE.projects : 6,
    pagination: true,
    sortable: true,
    // "CYYF", "5x35", "3x25+16" and "CYYF 3x25+16" all have to find the product.
    // The default column-by-column substring match could only ever answer the
    // first of those — see helpers/cableSearch.js jobSearchKey.
    globalFilterFn: (row, _columnId, value) =>
      matchesSearch(jobSearchKey(row.original), value)
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
        <h2 className="mb-0">
          <span className="me-3">Projects</span>{' '}
          <span className="fw-normal text-700">({jobs.length})</span>
        </h2>

        <Link
          className="btn btn-primary px-5"
          to="/apps/project-management/create-new"
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add new project
        </Link>

        {!fullPage && (
          <TableExpandButton
            to={FULL_TABLE_ROUTES.projects}
            className="ms-auto"
          />
        )}
      </div>
      {jobs.length > 0 ? (
        <>
          <ProjectsTopSection activeView="list" />
          <ProjectMonitorListTable />
        </>
      ) : (
        <div className="text-center py-6 text-500 fs-9">
          No jobs yet — create a new project or use Factory Sync to import existing production.
        </div>
      )}
    </AdvanceTableProvider>
  );
};

export default ProjectJobsMonitor;
